// Screen inventory (SP2) — the Leaflet map view (C1) + GPS override (C2).
// One marker per screen-with-GPS (divIcon = device-type icon inside a
// status-colored ring); click → popup with the screen's details + a link to
// detail + a "use device position" clear. Screens without a position are listed
// in a "no position" tray with a "place on map" action (click-to-place). Already
// positioned markers are draggable to override. OSM tiles + attribution.
// react-leaflet v4 (React 18). Every admin-set pin is a `manual` override
// (Phase A keeps device telemetry from overwriting it).
import { useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { PlayerSummary, Locale } from '@displaycms/contracts';
import { Icon, deviceTypeIcon, statusTone, type StatusToneName } from '@/components/icon';
import { StatusPill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export type SetGpsFn = (id: string, lat: number | null, lng: number | null) => void;

// Marker-ring color per semantic tone (inline — the divIcon HTML is injected by
// Leaflet outside React, so we don't rely on Tailwind purge keeping the class).
const TONE_COLOR: Record<StatusToneName, string> = {
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#0ea5e9',
  neutral: '#64748b',
};

function markerIcon(player: PlayerSummary): L.DivIcon {
  const status = player.status ?? 'idle';
  const color = TONE_COLOR[statusTone(status).tone];
  const svg = renderToStaticMarkup(
    <Icon name={deviceTypeIcon(player.device_type)} size={16} color="#0f172a" />,
  );
  return L.divIcon({
    className: 'screen-marker',
    html:
      `<div style="display:flex;align-items:center;justify-content:center;` +
      `width:30px;height:30px;border-radius:9999px;background:#fff;` +
      `border:3px solid ${color};box-shadow:0 1px 4px rgba(0,0,0,.35)">${svg}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
}

/** Click-to-place: while a screen is selected for positioning, the next map
 *  click sets its GPS. Must render INSIDE MapContainer (uses the map context). */
function ClickToPlace({
  activeId,
  onPlace,
}: {
  activeId: string | null;
  onPlace: (id: string, lat: number, lng: number) => void;
}): null {
  useMapEvents({
    click(e) {
      if (activeId) onPlace(activeId, e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function ScreenPopup({
  player,
  onClear,
}: {
  player: PlayerSummary;
  onClear?: SetGpsFn;
}): JSX.Element {
  const { t } = useTranslation(['players']);
  const status = player.status ?? 'idle';
  const path = player.location_path ?? [];
  const nowPlaying =
    status === 'running' ? (player.last_status?.current_program_id ?? null) : null;
  return (
    <div className="flex min-w-[180px] flex-col gap-1" data-testid={`popup-${player.id}`}>
      <div className="flex items-center gap-1.5">
        <Icon name={deviceTypeIcon(player.device_type)} size={16} />
        <span className="font-medium">{player.display_name}</span>
      </div>
      <StatusPill status={status} label={t(`players:status_badge.${status}`, { defaultValue: status })} />
      <span className="text-xs text-muted-foreground">
        {path.length > 0 ? path.join(' › ') : t('players:card.no_group')}
      </span>
      {nowPlaying && <span className="text-xs text-info">{t('players:now_playing')}</span>}
      <Link to={`/players/${player.id}`} className="text-xs font-medium text-primary hover:underline">
        {t('players:map.open_detail', { defaultValue: 'Voir le détail' })}
      </Link>
      {onClear && player.gps?.source === 'manual' && (
        <button
          type="button"
          data-testid={`gps-clear-${player.id}`}
          className="text-left text-xs text-muted-foreground hover:underline"
          onClick={() => onClear(player.id, null, null)}
        >
          {t('players:map.use_device_position', { defaultValue: 'Utiliser la position de l’appareil' })}
        </button>
      )}
    </div>
  );
}

/** The inventory map: markers for positioned screens + a tray for the rest.
 *  When `onSetGps` is supplied, markers are draggable, the popup can clear the
 *  pin, and tray screens can be click-placed on the map (manual override). */
export function InventoryMap({
  players,
  onSetGps,
}: {
  players: ReadonlyArray<PlayerSummary>;
  lang: Locale;
  onSetGps?: SetGpsFn;
}): JSX.Element {
  const { t } = useTranslation(['players']);
  const [positioningId, setPositioningId] = useState<string | null>(null);
  const withGps = players.filter((p) => p.gps);
  const withoutGps = players.filter((p) => !p.gps);
  const first = withGps[0];
  const center: [number, number] = first ? [first.gps!.lat, first.gps!.lng] : [20, 0];

  const place = (id: string, lat: number, lng: number): void => {
    onSetGps?.(id, lat, lng);
    setPositioningId(null);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="inventory-map">
      {positioningId && (
        <div
          data-testid="positioning-banner"
          className="rounded-md border border-primary bg-primary-subtle px-3 py-2 text-sm"
        >
          {t('players:map.click_to_place', { defaultValue: 'Cliquez sur la carte pour positionner l’écran' })}
          <button
            type="button"
            data-testid="positioning-cancel"
            className="ml-2 underline"
            onClick={() => setPositioningId(null)}
          >
            {t('common:cancel', { defaultValue: 'Annuler' })}
          </button>
        </div>
      )}
      <div className="h-[480px] overflow-hidden rounded-md border border-border">
        <MapContainer
          center={center}
          zoom={withGps.length > 0 ? 13 : 2}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {onSetGps && <ClickToPlace activeId={positioningId} onPlace={place} />}
          {withGps.map((p) => (
            <Marker
              key={p.id}
              position={[p.gps!.lat, p.gps!.lng]}
              icon={markerIcon(p)}
              draggable={Boolean(onSetGps)}
              {...(onSetGps
                ? {
                    eventHandlers: {
                      dragend: (e: L.LeafletEvent) => {
                        const ll = (e.target as L.Marker).getLatLng();
                        onSetGps(p.id, ll.lat, ll.lng);
                      },
                    },
                  }
                : {})}
              data-testid={`marker-${p.id}`}
            >
              <Popup>
                <ScreenPopup player={p} {...(onSetGps ? { onClear: onSetGps } : {})} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {withoutGps.length > 0 && (
        <div
          data-testid="no-position-tray"
          className="rounded-md border border-dashed border-border bg-card p-3"
        >
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Icon name="map-pin" size={14} className="text-muted-foreground" />
            {t('players:map.no_position', { defaultValue: 'Écrans sans position' })} ({withoutGps.length})
          </h3>
          <ul className="flex flex-wrap gap-2">
            {withoutGps.map((p) => (
              <li key={p.id} data-testid={`no-position-${p.id}`} className="flex items-center gap-1">
                <Link
                  to={`/players/${p.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs hover:bg-accent"
                >
                  <Icon name={deviceTypeIcon(p.device_type)} size={13} />
                  {p.display_name}
                </Link>
                {onSetGps && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`gps-place-${p.id}`}
                    onClick={() => setPositioningId(p.id)}
                  >
                    {t('players:map.place', { defaultValue: 'Positionner' })}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default InventoryMap;
